import React from 'react';
import { HexColorPicker } from 'react-colorful';
import { useSelector } from 'react-redux';
import { ActionButton, Button, DialogTrigger, Flex, Grid, repeat, View, } from '@adobe/react-spectrum';
import { toCSSColor } from '@printu/geom';
import { EditorActions } from 'app/stores/editor/actions';
import { useAppDispatch } from 'app/stores/store';
import { defaultColors } from 'app/stores/styles';
import { ColorButton } from 'app/views/components/core/ColorButton';
import { analytics } from 'app/utils/analytics';
import { call } from 'app/utils/function';
import { settings } from 'app/utils/settingsStorage';
const ColorEditor = (props) => {
    const { color: initialColor, excludedColors, onSelect, onCancel } = props;
    const [newColor, setNewColor] = React.useState(initialColor);
    const $onCancel = React.useCallback(() => call(onCancel), [onCancel]);
    const $onSelect = React.useCallback(() => call(onSelect, newColor), [newColor, onSelect]);
    const isColorExcluded = (color) => color === initialColor || excludedColors.indexOf(color) !== -1;
    return (React.createElement(DialogTrigger, { key: initialColor, type: "popover", placement: "top", isOpen: true, isDismissable: true },
        React.createElement(ColorButton, { color: initialColor }),
        React.createElement(View, { padding: "size-200" },
            React.createElement(Flex, { direction: "column", gap: "size-200" },
                React.createElement(HexColorPicker, { color: initialColor, onChange: setNewColor }),
                React.createElement(Flex, { direction: "row", gap: "size-100", justifyContent: "end" },
                    React.createElement(Button, { variant: "negative", isQuiet: true, onPress: $onCancel }, "Cancel"),
                    React.createElement(Button, { variant: "cta", isQuiet: true, isDisabled: isColorExcluded(newColor), onPress: $onSelect }, "Replace"))))));
};
export const ColorsEditor = () => {
    const dispatch = useAppDispatch();
    const colors = useSelector((state) => state.editor.colors);
    const [editedColor, setEditedColor] = React.useState(null);
    const dismissPicker = React.useCallback(() => setEditedColor(null), [setEditedColor]);
    const updatePalette = React.useCallback((newColor, updateIndex) => {
        const palette = colors.map((color, index) => index === updateIndex ? newColor : color);
        const serializedPalette = JSON.stringify(palette);
        // record changes
        analytics({
            action: 'settings:set',
            label: `colors:${serializedPalette}`,
        });
        // update globally
        dispatch(EditorActions.setColorsPalette({
            colors: palette,
            newColor,
        }));
        // set changed value in LC
        settings().set('colors', serializedPalette);
        dismissPicker();
    }, [colors, dispatch, dismissPicker]);
    return (React.createElement(Grid, { columns: repeat(colors.length, 'size-400'), rows: repeat(1, 'size-400'), justifyContent: "stretch", gap: "size-100" }, colors.map((swatchColor, swatchIndex) => {
        if (swatchColor === editedColor) {
            return (React.createElement(ColorEditor, { key: swatchColor, color: swatchColor, excludedColors: colors, onCancel: dismissPicker, onSelect: newColor => {
                    updatePalette(newColor, swatchIndex);
                } }));
        }
        return (React.createElement(ActionButton, { key: swatchColor, isQuiet: true, UNSAFE_style: {
                backgroundColor: toCSSColor(swatchColor),
                borderRadius: '8px',
            }, onPress: () => setEditedColor(swatchColor) }));
    })));
};
export const ColorsReset = () => {
    const dispatch = useAppDispatch();
    const colors = useSelector((state) => state.editor.colors);
    const resetColors = () => {
        // clear local storage
        settings().set('colors', undefined);
        // update colors
        dispatch(EditorActions.setColorsPalette({ colors: defaultColors }));
    };
    const hasDefaultPalette = colors.every((color, index) => color === defaultColors[index]);
    return (React.createElement(ActionButton, { isQuiet: true, isDisabled: hasDefaultPalette, onPress: resetColors }, "Reset"));
};
